import { DefaultMap, definedMap } from "@glideapps/ts-necessities";
import type { ChangeObservable } from "common/change-obserable";
import { AutoWatchable, Watchable } from "common/change-obserable";
import type { AppEnvironment } from "common/components/types";
import type { EminenceFlags, EminencesByApp } from "common/Database/eminence";
import { eminenceFull, freeFlags } from "common/Database/eminence";
import type { ComponentType, FunctionComponent } from "react";
import { getAppEminenceFlags, getOwnerEminenceFlags } from "store/types";
import type { Subtract } from "utility-types";
import { useAppID } from "./use-app-id";
import { useAppOwner } from "./use-app-owner";
import { useChangeObservable } from "./use-change-observable";
import { useDeepEqual } from "./use-deep-equal";

const ownerEminenceUpdatePeriod = 5 * 60 * 1000; // 5 minutes
const ownerEminenceRapidUpdatePeriod = 60 * 1000; // 60 seconds

type BuilderOwnerEminenceFlags = DefaultMap<string, Watchable<EminencesByApp>>;
type BuilderAppEminenceFlags = DefaultMap<string, Watchable<EminenceFlags>>;
const allWatchedFlags: BuilderOwnerEminenceFlags = new DefaultMap(k => {
    return k !== ""
        ? new AutoWatchable({}, async () => getOwnerEminenceFlags(k), ownerEminenceUpdatePeriod)
        : new Watchable({});
});

const appWatchedFlags: BuilderAppEminenceFlags = new DefaultMap(k => {
    return new AutoWatchable(eminenceFull, async () => getAppEminenceFlags(k), ownerEminenceUpdatePeriod);
});

// NOTE: If ownerID isn't specified, and the app owner's eminence has yet to be retrieved, this will return free flags.
export function getBuilderEminenceFlags(appID: string, ownerID: string | undefined): EminenceFlags {
    if (appWatchedFlags.has(appID)) {
        return appWatchedFlags.get(appID).current;
    }

    if (ownerID !== undefined) {
        return allWatchedFlags.get(ownerID).current[appID] ?? freeFlags;
    }

    // We don't know who the app belongs to, so we just check all owners.
    for (const watchable of allWatchedFlags.values()) {
        const flags = watchable.current[appID];
        if (flags !== undefined) {
            return flags;
        }
    }

    return freeFlags;
}

export async function updateBuilderEminenceFlags(ownerID: string): Promise<void> {
    const eminences = await getOwnerEminenceFlags(ownerID);
    allWatchedFlags.get(ownerID).current = eminences;
}

export async function updateBuilderEminenceFlagsForApp(appID: string): Promise<void> {
    const eminences = await getAppEminenceFlags(appID);
    appWatchedFlags.get(appID).current = eminences;
}

export function anticipateBuilderEminenceUpdate(ownerID: string) {
    if (ownerID === "") return;
    const watchableOnOwner: AutoWatchable<EminencesByApp> = allWatchedFlags.get(
        ownerID
    ) as AutoWatchable<EminencesByApp>;
    // This will set the update period until the user refreshes.
    // We'll accept this cost given that the vast majority of sessions will never upgrade.
    watchableOnOwner.setPeriod(ownerEminenceRapidUpdatePeriod);
}

export function useBuilderEminenceFlags(appID: string): EminenceFlags {
    const owner = useAppOwner(appID);
    let observable: ChangeObservable<any> | undefined = undefined;
    let extract = false;
    if (appWatchedFlags.has(appID)) {
        observable = appWatchedFlags.get(appID);
    } else {
        observable = definedMap(owner, o => allWatchedFlags.get(o.id));
        extract = true;
    }
    const observableResult = useChangeObservable(observable);
    const extracted = extract ? observableResult?.[appID] : observableResult;
    const appEminenceFlags = useDeepEqual(extracted);
    return appEminenceFlags ?? freeFlags;
}

export interface WithBuilderEminenceFlagsProps {
    readonly eminenceFlags: EminenceFlags;
}

interface WrapperProps {
    readonly appID: string;
    readonly appEnvironment: AppEnvironment;
}

interface Props extends Partial<WrapperProps>, WithBuilderEminenceFlagsProps {}

export const withBuilderEminenceFlags =
    <P extends Props>(Component: ComponentType<P>): FunctionComponent<Subtract<P, WithBuilderEminenceFlagsProps>> =>
    p => {
        const routerAppID = useAppID();
        const appID = p.appID ?? p.appEnvironment?.appID ?? routerAppID ?? "";
        const eminenceFlags = useBuilderEminenceFlags(appID);
        return <Component {...(p as P)} eminenceFlags={eminenceFlags} />;
    };
